
import { ChangeDetectionStrategy, Component, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

type HelpSection = 'start' | 'peaks' | 'search' | 'deep' | 'editor' | 'export';

@Component({
  selector: 'app-help-modal',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" (click)="close.emit()">
      <div class="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-w-5xl w-full h-full max-h-[90vh] flex flex-col" (click)="$event.stopPropagation()">
        <header class="flex justify-between items-center p-4 sm:p-6 border-b border-gray-700 flex-shrink-0 gap-3">
          <h2 class="text-2xl font-bold text-gray-100">Руководство пользователя</h2>
          <div class="flex items-center gap-2">
            <button (click)="restartTour.emit()"
                    class="px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-500/40 text-indigo-300 hover:bg-indigo-600/20 transition-colors flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              Запустить тур
            </button>
            <button (click)="close.emit()" class="text-gray-400 hover:text-white transition-colors p-1 -mt-2 -mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>

        <div class="flex flex-col md:flex-row flex-grow min-h-0">
          <!-- Navigation -->
          <nav class="flex-shrink-0 md:w-64 p-4 border-b md:border-b-0 md:border-r border-gray-700 overflow-y-auto">
            <ul class="space-y-2">
              <li><button (click)="setActiveSection('start')" [class]="getNavClass('start')">Начало работы</button></li>
              <li><button (click)="setActiveSection('peaks')" [class]="getNavClass('peaks')">Анализ по пикам</button></li>
              <li><button (click)="setActiveSection('search')" [class]="getNavClass('search')">Поиск моментов</button></li>
              <li><button (click)="setActiveSection('deep')" [class]="getNavClass('deep')">Глубокий анализ ИИ</button></li>
              <li><button (click)="setActiveSection('editor')" [class]="getNavClass('editor')">Редактор и плеер</button></li>
              <li><button (click)="setActiveSection('export')" [class]="getNavClass('export')">Экспорт</button></li>
            </ul>
          </nav>

          <!-- Content -->
          <div class="p-6 sm:p-8 space-y-6 overflow-y-auto flex-grow">
            @switch (activeSection()) {
              @case ('start') {
                <article class="prose prose-invert prose-sm sm:prose-base max-w-none">
                  <h3 class="text-xl font-semibold text-indigo-400">1. Начало работы: Загрузка файла</h3>
                  <p>Для начала анализа вам понадобится файл с логами чата стрима. Лучший инструмент для этого — <a href="https://github.com/lay295/TwitchDownloader" target="_blank">TwitchDownloader</a>.</p>
                  <ul>
                    <li><strong>JSON (Рекомендуется):</strong> Этот формат содержит всю информацию о стриме (имя стримера, название, игра, ID видео). Приложение считает эти данные автоматически.</li>
                    <li><strong>CSV:</strong> Более простой формат. Если вы используете его, вам нужно будет вручную ввести ссылку на видео или его ID, а также, по желанию, другие данные о стриме для более точного анализа ИИ.</li>
                  </ul>
                  <p>После выбора файла приложение автоматически обработает его и построит "тепловую карту" активности чата.</p>
                </article>
              }
              @case ('peaks') {
                <article class="prose prose-invert prose-sm sm:prose-base max-w-none">
                  <h3 class="text-xl font-semibold text-indigo-400">2. Анализ по пикам активности</h3>
                  <p>Это основной и самый быстрый способ найти потенциальные хайлайты. Он работает в два этапа:</p>
                  <ol>
                    <li>
                      <strong>Поиск пиков:</strong> Приложение находит моменты, когда в чате было аномально много сообщений. Вы можете контролировать этот процесс с помощью ползунка <strong>"Чувствительность поиска пиков"</strong>.
                      <ul>
                        <li><strong>Низкое значение:</strong> Найдется много мелких всплесков, возможно, не очень значимых.</li>
                        <li><strong>Высокое значение:</strong> Будут найдены только самые крупные и массовые реакции чата.</li>
                      </ul>
                       <p>Найденные пики подсвечиваются желтым или красным на тепловой карте.</p>
                    </li>
                    <li>
                      <strong>Анализ ИИ:</strong> После того как вы определились с чувствительностью, нажмите кнопку <strong>"Анализировать моменты с ИИ"</strong>. Приложение отправит сообщения из найденных пиковых моментов в нейросеть, которая для каждого момента определит:
                      <ul>
                        <li><strong>Настроение:</strong> Общая эмоция чата (позитив, негатив, восторг и т.д.).</li>
                        <li><strong>Предположение о событии:</strong> Что, скорее всего, произошло на стриме в этот момент.</li>
                        <li><strong>Краткое резюме:</strong> Обобщение того, что обсуждали в чате.</li>
                      </ul>
                    </li>
                  </ol>
                  <p class="font-semibold">Совет: Экспериментируйте с чувствительностью. Иногда интересные моменты скрываются в не самых больших пиках.</p>
                </article>
              }
              @case ('search') {
                <article class="prose prose-invert prose-sm sm:prose-base max-w-none">
                  <h3 class="text-xl font-semibold text-indigo-400">3. Поиск моментов по словам</h3>
                  <p>Вкладка <strong>«Поиск»</strong> в правой панели позволяет найти моменты стрима по конкретным словам в чате — без запуска ИИ-анализа. Результаты появляются в виде хайлайтов с амбровой рамкой и бейджем <code>🔍 запрос</code>.</p>

                  <h4 class="text-lg font-semibold text-amber-300 mt-4">Синтаксис</h4>
                  <table class="text-sm w-full my-2">
                    <thead>
                      <tr class="border-b border-gray-700"><th class="text-left py-1 pr-4 text-amber-300">Запрос</th><th class="text-left py-1 text-gray-400">Что делает</th></tr>
                    </thead>
                    <tbody>
                      <tr><td class="py-1.5 pr-4 font-mono text-amber-300 align-top whitespace-nowrap">мина</td><td class="py-1.5">Находит также мине, мину, мины, минами — через морфологический стеммер.</td></tr>
                      <tr><td class="py-1.5 pr-4 font-mono text-amber-300 align-top whitespace-nowrap">мина, граната</td><td class="py-1.5"><strong>ИЛИ</strong> — сообщение должно содержать любое из слов. <code>|</code> работает так же: <code>мина|граната</code>.</td></tr>
                      <tr><td class="py-1.5 pr-4 font-mono text-amber-300 align-top whitespace-nowrap">мина граната</td><td class="py-1.5"><strong>И</strong> — оба слова в одном сообщении.</td></tr>
                      <tr><td class="py-1.5 pr-4 font-mono text-amber-300 align-top whitespace-nowrap">"ну всё"</td><td class="py-1.5">Точная подстрока — без стеммера, с учётом пробелов.</td></tr>
                      <tr><td class="py-1.5 pr-4 font-mono text-amber-300 align-top whitespace-nowrap">-бот</td><td class="py-1.5">Исключить сообщения с этим словом (стеммер тоже сработает: также бота, боту, боты).</td></tr>
                      <tr><td class="py-1.5 pr-4 font-mono text-amber-300 align-top whitespace-nowrap">-"мин"</td><td class="py-1.5"><strong>Точное</strong> исключение — ищется буква в букву, без стеммера. Полезно, когда позитивный запрос и негативный имеют общий корень: <code>мина -"мин"</code> даст формы мины, но отсеет голое «мин».</td></tr>
                      <tr><td class="py-1.5 pr-4 font-mono text-amber-300 align-top whitespace-nowrap">мина, граната бот -я</td><td class="py-1.5">Комбинация: (мина ИЛИ граната) И «бот» И НЕ «я».</td></tr>
                    </tbody>
                  </table>

                  <h4 class="text-lg font-semibold text-amber-300 mt-4">Чекбокс «точное совпадение»</h4>
                  <p>По умолчанию включена морфология — одно слово ловит все свои формы. Если нужно наоборот, найти строго «мина» и ничего больше, включите чекбокс. Активный запрос при этом перезапустится автоматически.</p>

                  <h4 class="text-lg font-semibold text-amber-300 mt-4">Тепловая карта</h4>
                  <p>Когда запрос активен, bin'ы чата на тепловой карте (под плеером) окрашиваются амбровым цветом с интенсивностью, пропорциональной количеству совпадений. Остальные bin'ы затемняются — так видно, где именно в VOD'e концентрируются упоминания.</p>

                  <h4 class="text-lg font-semibold text-amber-300 mt-4">Просмотр сообщений момента</h4>
                  <p>Клик по амбровому бейджу <code>🔍 запрос · N</code> на карточке открывает поп-ап с примерами сообщений этого момента. Совпавшие слова подсвечены. Закрывается кликом вне окошка, кнопкой <kbd>×</kbd> или <kbd>Esc</kbd>.</p>

                  <h4 class="text-lg font-semibold text-amber-300 mt-4">Закрепление в общий список</h4>
                  <p>Результаты поиска живут во вкладке «Поиск» и не смешиваются с моментами из анализа по пикам/ИИ. Чтобы перенести понравившийся момент в общий список (вкладку «Все»), нажмите на карточке большую амбровую кнопку <strong>«Закрепить в общий список»</strong> — или горячую клавишу <kbd>.</kbd> (Ю). Закреплённый момент появится в «Все» с пометкой «🔖 Закреплён из поиска» и переживёт смену запроса. Повторное нажатие снимает закрепление.</p>

                  <p class="mt-4"><strong>Совет:</strong> История последних 10 запросов сохраняется между сессиями — кнопки истории появляются под полем ввода, когда активного запроса нет.</p>
                </article>
              }
              @case ('deep') {
                 <article class="prose prose-invert prose-sm sm:prose-base max-w-none">
                  <h3 class="text-xl font-semibold text-indigo-400">4. Глубокий анализ ИИ</h3>
                  <p>Кнопка <strong>"Найти скрытые моменты с ИИ"</strong> запускает более сложный и медленный анализ. В этом режиме ИИ просматривает <em>весь лог чата</em>, а не только пики активности.</p>
                  <p><strong>Цель этого анализа</strong> — найти неочевидные, но интересные события, которые не всегда вызывают огромный всплеск сообщений. Например:</p>
                  <ul>
                    <li>Появление уникальной копипасты, связанной с событием на стриме.</li>
                    <li>Массовый, но короткий смех или удивление в чате.</li>
                    <li>Интересные вопросы к стримеру, которые повторяются несколькими людьми.</li>
                    <li>Коллективные эмоции (например, спам "F" в чате).</li>
                  </ul>
                  <p>Этот режим отлично дополняет анализ по пикам и помогает найти "скрытые жемчужины". Моменты, найденные этим способом, помечаются специальным значком ✨ в списке.</p>
                </article>
              }
              @case ('editor') {
                 <article class="prose prose-invert prose-sm sm:prose-base max-w-none">
                  <h3 class="text-xl font-semibold text-indigo-400">5. Редактор и плеер</h3>
                  <p>Для быстрой работы (левая рука на клавиатуре, правая на мыши) используйте горячие клавиши:</p>
                  <ul class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <li><kbd>J</kbd> (О) / <kbd>L</kbd> (Д) — Перемотка на 10 сек назад / вперед.</li>
                    <li><kbd>Shift</kbd> + <kbd>J</kbd> / <kbd>L</kbd> — Перемотка на 30 сек назад / вперед.</li>
                    <li><kbd>←</kbd> / <kbd>→</kbd> — Перемотка на 5 сек назад / вперед.</li>
                    <li><kbd>K</kbd> (Л) или <kbd>Space</kbd> — Пауза / Старт.</li>
                    <li><kbd>I</kbd> (Ш) / <kbd>O</kbd> (Щ) — Установить начало / конец клипа.</li>
                    <li><kbd>Shift</kbd> + <kbd>I</kbd> / <kbd>O</kbd> — Перейти к началу / концу клипа.</li>
                    <li><kbd>Z</kbd> (Я) — Приблизить хитмапу к клипу (±30 сек) / вернуть прежний масштаб.</li>
                    <li><kbd>,</kbd> (Б) — Удалить момент (или восстановить).</li>
                    <li><kbd>.</kbd> (Ю) — Закрепить момент (в «Поиске» — перенести в общий список).</li>
                    <li><kbd>M</kbd> (Ь) — Объединить выбранные моменты (checkbox).</li>
                    <li><kbd>↑</kbd> / <kbd>↓</kbd> — Выбрать предыдущий / следующий момент в списке.</li>
                    <li><kbd>Alt</kbd> + <kbd>I</kbd> / <kbd>O</kbd> — Начало / конец области интереса по плееру.</li>
                    <li><kbd>Alt</kbd> + <kbd>P</kbd> (З) — Включить / выключить область интереса.</li>
                    <li><kbd>Ctrl</kbd> + <kbd>Z</kbd> — Отменить последнее действие.</li>
                    <li><kbd>Ctrl</kbd> + <kbd>Y</kbd> или <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>Z</kbd> — Повторить отменённое.</li>
                  </ul>
                  <p class="text-sm mt-2"><strong>Отмена</strong> — также кнопки ↶ / ↷ над списком моментов. Отменяются правки моментов (избранное, удаление, перенос во «Все», края клипа, объединение), Pre-Roll, чувствительность, фильтры чата, область интереса и поиск по словам. Результаты ИИ-анализа отдельным шагом не отменяются, ваши правки поверх них — да. У каждого стрима своя история; она живёт до перезагрузки страницы. В текстовом поле Ctrl+Z отменяет ввод текста.</p>
                  <p class="text-sm mt-2"><strong>Область интереса</strong> — кнопка слева от хитмапы. Пока она включена, списки и экспорт показывают только моменты внутри области (Избранное — всегда), а все виды анализа сканируют только её. Края области перетаскиваются мышью.</p>
                  <p class="text-xs text-gray-500 mt-2">Горячие клавиши работают на любой раскладке (по физическому положению клавиши) — в скобках указан символ русской ЙЦУКЕН. Исключение — Ctrl+Z / Ctrl+Y: по букве на клавише (на русской раскладке — Я / Н), на Mac — ⌘Z / ⇧⌘Z.</p>
                  <div class="mt-4">
                    <strong>Редактор:</strong>
                    <ul>
                       <li><strong>Смещение перемотки:</strong> Позволяет настроить, за сколько секунд до начала пика активности начнётся воспроизведение (при выборе клипа из списка).</li>
                       <li><strong>Шкала над хитмапой:</strong> клик — перейти в это место, протянуть с зажатой кнопкой — найти место (плеер перемотается, когда отпустите). Красная линия — где сейчас плеер.</li>
                       <li><strong>Клип:</strong> синяя полоса на шкале — границы выбранного клипа, её края перетаскиваются мышью (липнут к красной линии). Для точной правки — <kbd>Z</kbd>, двойной клик по клипу или кнопка-лупа слева от хитмапы.</li>
                    </ul>
                  </div>
                </article>
              }
              @case ('export') {
                 <article class="prose prose-invert prose-sm sm:prose-base max-w-none">
                  <h3 class="text-xl font-semibold text-indigo-400">6. Экспорт результатов</h3>
                  <p>После того как вы отредактировали все моменты, вы можете экспортировать их. Экспортируются все моменты, кроме тех, что находятся во вкладке "Удалённые".</p>
                  <ol>
                    <li>
                      <strong>Экспорт в .TXT:</strong> Создает текстовый файл с подробным отчетом. Для каждого клипа указывается таймкод, резюме от ИИ и прямая ссылка на момент в VOD на сайте Twitch. Отлично подходит для контент-плана или для того, чтобы поделиться с кем-то.
                    </li>
                    <li>
                      <strong>Скрипт для загрузки:</strong> Генерирует исполняемый файл (.bat для Windows или .sh для macOS/Linux), который автоматически скачает все отмеченные клипы с помощью <a href="https://github.com/lay295/TwitchDownloader" target="_blank">TwitchDownloader CLI</a>.
                      <div class="p-3 my-2 bg-yellow-900/50 border border-yellow-700 rounded-md text-yellow-300">
                        <strong>Важно:</strong> для работы скриптов у вас должен быть установлен TwitchDownloader CLI и <a href="#" (click)="onOpenPathHelp($event)">прописан в системную переменную PATH</a>.
                      </div>
                      Скрипт создаст папку 'clips' рядом с собой и сохранит в неё все видео.
                    </li>
                  </ol>
                </article>
              }
            }
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .prose a {
      color: #818cf8;
      text-decoration: underline;
    }
    .prose kbd {
      padding: 0.25rem 0.5rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: #d1d5db;
      background-color: #4b5563;
      border: 1px solid #6b7280;
      border-radius: 0.375rem;
    }
  `]
})
export class HelpModalComponent {
  @Output() close = new EventEmitter<void>();
  @Output() openPathHelp = new EventEmitter<void>();
  @Output() restartTour = new EventEmitter<void>();

  activeSection = signal<HelpSection>('start');

  setActiveSection(section: HelpSection): void {
    this.activeSection.set(section);
  }

  getNavClass(section: HelpSection): string {
    const baseClasses = 'w-full text-left px-4 py-2 rounded-md transition-colors text-sm font-medium';
    if (this.activeSection() === section) {
      return `${baseClasses} bg-indigo-600 text-white`;
    }
    return `${baseClasses} text-gray-300 hover:bg-gray-700 hover:text-white`;
  }

  onOpenPathHelp(event: Event): void {
    event.preventDefault();
    this.openPathHelp.emit();
  }
}
